import { chmod, rename, unlink } from "node:fs/promises";
import { createConnection, createServer, type Socket } from "node:net";

import { sidecarEndpoint } from "./sidecar-endpoint.js";
import {
  SIDECAR_PROTOCOL_VERSION,
  type SidecarEventMessage,
  type SidecarNotificationMessage,
  type SidecarOutboundMessage,
  type SidecarRequestMessage,
  type SidecarResponseMessage,
} from "./sidecar-protocol.js";
import { MentisSidecarRuntime } from "./sidecar-runtime.js";
import { encodeSidecarFrame, SidecarFrameDecoder } from "./sidecar-wire.js";

const endpoint = sidecarEndpoint();
const clients = new Set<Socket>();
const decoders = new Map<Socket, SidecarFrameDecoder>();
const sessionOwners = new Map<string, Socket>();
const LAST_CLIENT_GRACE_MS = 250;
let stopping = false;
let idleTimer: ReturnType<typeof setTimeout> | undefined;

function send(
  socket: Socket,
  message: SidecarResponseMessage | SidecarEventMessage,
  callback?: () => void,
): void {
  if (socket.destroyed || !socket.writable) {
    callback?.();
    return;
  }
  try {
    socket.write(encodeSidecarFrame(message), callback);
  } catch {
    socket.destroy(new Error("Mentis sidecar could not encode an IPC frame"));
    callback?.();
  }
}

function eventSessionId(event: SidecarEventMessage["event"]): string | undefined {
  if (event.name === "capsule.updated") return event.capsule.sessionId;
  if (
    event.name === "active-context.updated" ||
    event.name === "context.updated" ||
    event.name === "reason.request" ||
    event.name === "cognition.request" ||
    event.name === "cognition.cancel"
  ) {
    return event.clientSessionId;
  }
  return undefined;
}

const runtime = new MentisSidecarRuntime((event) => {
  const message: SidecarEventMessage = {
    type: "event",
    protocolVersion: SIDECAR_PROTOCOL_VERSION,
    event,
  };
  const sessionId = eventSessionId(event);
  const owner = sessionId === undefined ? undefined : sessionOwners.get(sessionId);
  if (owner !== undefined) {
    send(owner, message);
    return;
  }
  for (const client of clients) send(client, message);
});

const server = createServer((socket) => {
  if (stopping) {
    socket.destroy();
    return;
  }
  if (idleTimer !== undefined) clearTimeout(idleTimer);
  idleTimer = undefined;
  socket.setNoDelay(true);
  clients.add(socket);
  decoders.set(socket, new SidecarFrameDecoder());
  send(socket, {
    type: "event",
    protocolVersion: SIDECAR_PROTOCOL_VERSION,
    event: { name: "ready", version: String(SIDECAR_PROTOCOL_VERSION), pid: process.pid },
  });
  socket.on("data", (chunk: Buffer) => {
    try {
      for (const message of decoders.get(socket)?.push(chunk) ?? []) {
        handleMessage(socket, message as SidecarOutboundMessage);
      }
    } catch (error) {
      socket.destroy(error instanceof Error ? error : new Error(String(error)));
    }
  });
  socket.on("error", () => undefined);
  socket.on("close", () => detachClient(socket));
});

function handleMessage(socket: Socket, value: SidecarOutboundMessage): void {
  if (value.protocolVersion !== SIDECAR_PROTOCOL_VERSION) {
    socket.destroy(new Error("Mentis sidecar protocol mismatch"));
    return;
  }
  if (value.type === "notification") {
    handleNotification(socket, value);
    return;
  }
  handleRequest(socket, value);
}

function handleNotification(socket: Socket, value: SidecarNotificationMessage): void {
  if (value.notification.method === "shutdown") {
    void shutdown();
    return;
  }
  runtime.notify(value.notification);
  if (value.notification.method === "session.close") {
    const sessionId = value.notification.params.clientSessionId;
    if (sessionOwners.get(sessionId) === socket) sessionOwners.delete(sessionId);
  }
}

function handleRequest(socket: Socket, value: SidecarRequestMessage): void {
  if (value.request.method === "shutdown") {
    send(
      socket,
      {
        type: "response",
        id: value.id,
        protocolVersion: SIDECAR_PROTOCOL_VERSION,
        result: { closed: true },
      },
      () => void shutdown(),
    );
    return;
  }
  const openedSessionId =
    value.request.method === "session.open" ? value.request.params.clientSessionId : undefined;
  if (openedSessionId !== undefined) sessionOwners.set(openedSessionId, socket);
  void runtime.request(value.request).then(
    (result) => {
      const response: SidecarResponseMessage = {
        type: "response",
        id: value.id,
        protocolVersion: SIDECAR_PROTOCOL_VERSION,
        result,
      };
      send(socket, response);
    },
    (error) => {
      if (openedSessionId !== undefined && sessionOwners.get(openedSessionId) === socket) {
        sessionOwners.delete(openedSessionId);
      }
      send(socket, {
        type: "response",
        id: value.id,
        protocolVersion: SIDECAR_PROTOCOL_VERSION,
        error: error instanceof Error ? error.message : String(error),
      });
    },
  );
}

function detachClient(socket: Socket): void {
  clients.delete(socket);
  decoders.delete(socket);
  for (const [sessionId, owner] of sessionOwners) {
    if (owner !== socket) continue;
    sessionOwners.delete(sessionId);
    runtime.notify({ method: "session.close", params: { clientSessionId: sessionId } });
  }
  if (!stopping && clients.size === 0) scheduleIdleShutdown(LAST_CLIENT_GRACE_MS);
}

function scheduleIdleShutdown(delayMs: number): void {
  if (idleTimer !== undefined) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    idleTimer = undefined;
    if (clients.size === 0) void shutdown();
  }, delayMs);
}

async function shutdown(): Promise<void> {
  if (stopping) return;
  stopping = true;
  if (idleTimer !== undefined) clearTimeout(idleTimer);
  idleTimer = undefined;
  for (const socket of clients) socket.end();
  await runtime.close().catch(() => undefined);
  await new Promise<void>((resolve) => server.close(() => resolve()));
  if (process.platform !== "win32") await unlink(endpoint).catch(() => undefined);
  process.exit(0);
}

function listen(): Promise<void> {
  return new Promise((resolve, reject) => {
    const onError = (error: Error) => {
      server.off("listening", onListening);
      reject(error);
    };
    const onListening = () => {
      server.off("error", onError);
      resolve();
    };
    server.once("error", onError);
    server.once("listening", onListening);
    server.listen(endpoint);
  });
}

async function endpointIsActive(): Promise<boolean> {
  return await new Promise<boolean>((resolve) => {
    const socket = createConnection(endpoint);
    let settled = false;
    const finish = (active: boolean) => {
      if (settled) return;
      settled = true;
      socket.destroy();
      resolve(active);
    };
    socket.once("connect", () => finish(true));
    socket.once("error", () => finish(false));
    socket.setTimeout(250, () => finish(false));
  });
}

async function bindSingleton(): Promise<boolean> {
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      await listen();
      if (process.platform !== "win32") await chmod(endpoint, 0o600);
      return true;
    } catch (error) {
      const code = (error as NodeJS.ErrnoException).code;
      if (code !== "EADDRINUSE" && code !== "EEXIST") throw error;
      if (await endpointIsActive()) return false;
      if (process.platform === "win32") continue;
      const stale = `${endpoint}.stale-${process.pid}-${Date.now()}`;
      await rename(endpoint, stale)
        .then(() => unlink(stale).catch(() => undefined))
        .catch((renameError: unknown) => {
          if ((renameError as NodeJS.ErrnoException).code !== "ENOENT") throw renameError;
        });
    }
  }
  throw new Error(`Mentis sidecar could not claim singleton endpoint ${endpoint}`);
}

process.on("SIGTERM", () => void shutdown());
process.on("SIGINT", () => void shutdown());

if (await bindSingleton()) {
  // A launcher can disappear before it connects. Do not leave an unowned daemon.
  scheduleIdleShutdown(10_000);
} else {
  process.exit(0);
}
